import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API, formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Sparkles, Calendar, Phone, Mail, User, CheckCircle2, ChevronLeft, ChevronRight } from "lucide-react";
import AIChatWidget from "@/components/AIChatWidget";
import { usePublicSettings } from "@/hooks/usePublicSettings";

// Use unauthenticated axios for public endpoints
const publicApi = axios.create({ baseURL: API });

const CLINIC_TZ = "Europe/Tirane";

// ─── i18n ──────────────────────────────────────────────────────────────────
const I18N = {
  sq: {
    locale: "sq-AL",
    daysShort: ["Hë","Ma","Më","En","Pr","Sh","Di"],
    months: ["Janar","Shkurt","Mars","Prill","Maj","Qershor","Korrik","Gusht","Shtator","Tetor","Nëntor","Dhjetor"],
    monthsShort: ["Jan","Shk","Mar","Pri","Maj","Qer","Kor","Gus","Sht","Tet","Nën","Dhj"],
    onlineBooking: "Rezervim Online",
    clinicLabel: "Klinikë Dentare",
    title: "Rezervoni Takimin Tuaj",
    subtitle: "Zgjidhni ditën dhe orarin që ju përshtatet. Pas rezervimit, klinika do t'ju kontaktojë për konfirmim.",
    staffLogin: "Hyrje për stafin →",
    step1: "Hapi 1",
    pickDate: "Zgjidhni Datën",
    step2: "Hapi 2",
    yourDetails: "Të Dhënat Tuaja",
    firstName: "Emri",
    lastName: "Mbiemri",
    phone: "Telefoni",
    phonePh: "+355 69 xxx xxxx",
    email: "Email (opsionale)",
    reason: "Arsyeja e Vizitës",
    reasonPh: "P.sh. Kontroll, dhimbje, pastrim",
    summary: "Përmbledhje",
    apptOn: (date, time) => <>Takim më <strong>{date}</strong> në orën <strong>{time}</strong></>,
    pickSlotFirst: "Zgjidhni një orar fillimisht",
    confirm: "Konfirmo Rezervimin",
    booking: "Duke rezervuar…",
    consent: "Duke konfirmuar, pranoni që klinika t'ju kontaktojë për detajet e takimit.",
    searchingSlots: "Duke kërkuar orarët…",
    clinicClosed: "Klinika është e mbyllur këtë ditë.",
    noSlots: "Nuk ka orare të lira për këtë ditë. Provoni një datë tjetër.",
    pickSlotError: "Ju lutem zgjidhni një orar",
    bookingError: "Gabim gjatë rezervimit",
    busy: "I zënë",
    breakLunch: "Pushim drekë",
    outsideHours: "Jashtë orarit",
    legendFree: "I lirë",
    legendSelected: "I zgjedhur",
    legendBusy: "I zënë / Pushim",
    lunch: "Drekë",
    confirmedEyebrow: "Rezervim i Konfirmuar",
    thanks: "Faleminderit!",
    confirmedMsgPre: "Takimi juaj është rezervuar për",
    contactNote: (phone) => <>Klinika do t&apos;ju kontaktojë në numrin <span className="font-mono">{phone}</span> për konfirmimin përfundimtar.</>,
    bookAnother: "Bëj një rezervim tjetër",
    footer: (brand, year) => `${brand} · Rezervim Dentar · © ${year}`,
  },
  en: {
    locale: "en-GB",
    daysShort: ["Mo","Tu","We","Th","Fr","Sa","Su"],
    months: ["January","February","March","April","May","June","July","August","September","October","November","December"],
    monthsShort: ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"],
    onlineBooking: "Online Booking",
    clinicLabel: "Dental Clinic",
    title: "Book Your Appointment",
    subtitle: "Pick the day and time that suits you. After booking, the clinic will contact you for confirmation.",
    staffLogin: "Staff login →",
    step1: "Step 1",
    pickDate: "Pick a Date",
    step2: "Step 2",
    yourDetails: "Your Details",
    firstName: "First Name",
    lastName: "Last Name",
    phone: "Phone",
    phonePh: "+355 69 xxx xxxx",
    email: "Email (optional)",
    reason: "Reason for Visit",
    reasonPh: "E.g. Check-up, pain, cleaning",
    summary: "Summary",
    apptOn: (date, time) => <>Appointment on <strong>{date}</strong> at <strong>{time}</strong></>,
    pickSlotFirst: "Please pick a slot first",
    confirm: "Confirm Booking",
    booking: "Booking…",
    consent: "By confirming, you agree the clinic may contact you about your appointment.",
    searchingSlots: "Looking up slots…",
    clinicClosed: "The clinic is closed on this day.",
    noSlots: "No free slots for this day. Try another date.",
    pickSlotError: "Please pick a time slot",
    bookingError: "Booking failed",
    busy: "Busy",
    breakLunch: "Lunch break",
    outsideHours: "Outside hours",
    legendFree: "Free",
    legendSelected: "Selected",
    legendBusy: "Busy / Break",
    lunch: "Lunch",
    confirmedEyebrow: "Booking Confirmed",
    thanks: "Thank you!",
    confirmedMsgPre: "Your appointment is booked for",
    contactNote: (phone) => <>The clinic will contact you on <span className="font-mono">{phone}</span> for final confirmation.</>,
    bookAnother: "Make another booking",
    footer: (brand, year) => `${brand} · Dental Booking · © ${year}`,
  },
  it: {
    locale: "it-IT",
    daysShort: ["Lu","Ma","Me","Gi","Ve","Sa","Do"],
    months: ["Gennaio","Febbraio","Marzo","Aprile","Maggio","Giugno","Luglio","Agosto","Settembre","Ottobre","Novembre","Dicembre"],
    monthsShort: ["Gen","Feb","Mar","Apr","Mag","Giu","Lug","Ago","Set","Ott","Nov","Dic"],
    onlineBooking: "Prenotazione Online",
    clinicLabel: "Clinica Dentale",
    title: "Prenota il tuo Appuntamento",
    subtitle: "Scegli il giorno e l'orario che preferisci. Dopo la prenotazione, la clinica ti contatterà per la conferma.",
    staffLogin: "Accesso staff →",
    step1: "Passo 1",
    pickDate: "Scegli la Data",
    step2: "Passo 2",
    yourDetails: "I tuoi Dati",
    firstName: "Nome",
    lastName: "Cognome",
    phone: "Telefono",
    phonePh: "+355 69 xxx xxxx",
    email: "Email (opzionale)",
    reason: "Motivo della Visita",
    reasonPh: "Es. Controllo, dolore, pulizia",
    summary: "Riepilogo",
    apptOn: (date, time) => <>Appuntamento il <strong>{date}</strong> alle <strong>{time}</strong></>,
    pickSlotFirst: "Seleziona prima un orario",
    confirm: "Conferma Prenotazione",
    booking: "Prenotazione…",
    consent: "Confermando, accetti che la clinica ti contatti per i dettagli dell'appuntamento.",
    searchingSlots: "Ricerca orari…",
    clinicClosed: "La clinica è chiusa in questo giorno.",
    noSlots: "Nessuno slot libero per questo giorno. Prova un'altra data.",
    pickSlotError: "Seleziona un orario",
    bookingError: "Errore nella prenotazione",
    busy: "Occupato",
    breakLunch: "Pausa pranzo",
    outsideHours: "Fuori orario",
    legendFree: "Libero",
    legendSelected: "Selezionato",
    legendBusy: "Occupato / Pausa",
    lunch: "Pranzo",
    confirmedEyebrow: "Prenotazione Confermata",
    thanks: "Grazie!",
    confirmedMsgPre: "Il tuo appuntamento è prenotato per il",
    contactNote: (phone) => <>La clinica ti contatterà al <span className="font-mono">{phone}</span> per la conferma finale.</>,
    bookAnother: "Fai un'altra prenotazione",
    footer: (brand, year) => `${brand} · Prenotazione Dentale · © ${year}`,
  },
};

// Auto-detekto gjuhen nga ?host= ose nga window.location.hostname
function detectLangFromHost(forcedLang) {
  const valid = ["sq", "en", "it"];
  if (forcedLang && valid.includes(forcedLang)) return forcedLang;
  if (typeof window === "undefined") return "sq";
  let host = "";
  try {
    const params = new URLSearchParams(window.location.search);
    host = (params.get("host") || window.location.hostname || "").toLowerCase();
  } catch (e) {
    host = (window.location.hostname || "").toLowerCase();
  }
  if (host.endsWith(".it")) return "it";
  if (host.endsWith(".com") || host.endsWith(".net") || host.endsWith(".org") || host.endsWith(".co.uk")) return "en";
  return "sq"; // default + .al + everything else
}

// ─── Brand themes (Zani Dental aesthetic) ──────────────────────────────────
// Aplikohet vetem ne embed mode per integrim te white-label me website-t e klienteve
const ZANI_THEME = {
  bg: "transparent",
  card: "#FFFFFF",
  cardBorder: "#EAE6DF",
  primary: "#0e5b50",        // jeshile-blu e errët (accent i Zani)
  primaryHover: "#0a4a40",
  textOnPrimary: "#FFFFFF",
  text: "#1d1d1d",
  textMuted: "#858585",
  surface: "#faf8f5",
  surfaceWarm: "#f3ede5",
  error: "#e65757",
  fontStack: "'Poppins', system-ui, -apple-system, sans-serif",
};

export default function PublicBooking() {
  const branding = usePublicSettings();

  // ── Embed + i18n ────────────────────────────────────────────────────────
  const queryParams = useMemo(() => {
    if (typeof window === "undefined") return new URLSearchParams();
    return new URLSearchParams(window.location.search);
  }, []);
  const isEmbed = queryParams.has("embed");
  const forcedLang = queryParams.get("lang");
  const [lang, setLang] = useState(() => detectLangFromHost(forcedLang));
  const t = I18N[lang] || I18N.sq;
  const theme = ZANI_THEME; // Aktualisht nje teme e vetme premium; mund te shtohen me vone

  const fmtTime = (iso) => new Date(iso).toLocaleTimeString(t.locale, { hour: "2-digit", minute: "2-digit", hour12: false, timeZone: CLINIC_TZ });
  const fmtDateLong = (d) => `${d.getDate()} ${t.months[d.getMonth()]} ${d.getFullYear()}`;

  // Set HTML title for white-label
  useEffect(() => {
    if (branding?.clinic_name) {
      document.title = `${branding.clinic_name} · ${t.onlineBooking}`;
    }
  }, [branding.clinic_name, t.onlineBooking]);

  // Embed-only: ridimensionim automatik + dokumenti i tejdukshem
  useEffect(() => {
    if (!isEmbed) return;
    // Bej HTML+body te tejdukshem dhe pa skroll te brendshem
    const prevBodyBg = document.body.style.background;
    const prevHtmlBg = document.documentElement.style.background;
    const prevBodyOverflow = document.body.style.overflow;
    document.body.style.background = "transparent";
    document.documentElement.style.background = "transparent";
    document.body.style.overflow = "hidden";
    const post = () => {
      try {
        window.parent.postMessage({ source: "dentart-booking", height: document.body.scrollHeight }, "*");
      } catch (e) { /* parent may be cross-origin during early load */ }
    };
    post();
    const ro = new ResizeObserver(post);
    ro.observe(document.body);
    return () => {
      ro.disconnect();
      document.body.style.background = prevBodyBg;
      document.documentElement.style.background = prevHtmlBg;
      document.body.style.overflow = prevBodyOverflow;
    };
  }, [isEmbed]);

  const [selectedDate, setSelectedDate] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() + 1); d.setHours(0,0,0,0); return d;
  });
  const [slots, setSlots] = useState([]);
  const [allSlots, setAllSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [form, setForm] = useState({ first_name: "", last_name: "", phone: "", email: "", reason: "" });
  const [submitting, setSubmitting] = useState(false);
  const [confirmation, setConfirmation] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoadingSlots(true);
      setSelectedSlot(null);
      try {
        const d = selectedDate;
        const ymd = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
        const { data } = await publicApi.get(`/public/availability`, { params: { date: ymd } });
        if (cancelled) return;
        setSlots(data.slots || []);
        setAllSlots(data.all_slots || []);
      } catch {
        if (cancelled) return;
        setSlots([]);
        setAllSlots([]);
      } finally {
        if (!cancelled) setLoadingSlots(false);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedDate]);

  const nextDays = Array.from({ length: 7 }, (_, i) => {
    const d = new Date();
    d.setHours(0,0,0,0);
    d.setDate(d.getDate() + 1 + i);
    return d;
  });

  const moveWeek = (delta) => {
    const d = new Date(selectedDate); d.setDate(d.getDate() + delta * 7); setSelectedDate(d);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!selectedSlot) { setError(t.pickSlotError); return; }
    setError("");
    setSubmitting(true);
    try {
      const { data } = await publicApi.post(`/public/book`, {
        ...form, start_time: selectedSlot,
      });
      setConfirmation(data);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || t.bookingError);
    } finally { setSubmitting(false); }
  };

  // ─── Style helpers per embed mode ────────────────────────────────────────
  const embedStyles = isEmbed ? {
    fontFamily: theme.fontStack,
    color: theme.text,
    background: theme.bg,
  } : {};
  const embedCardStyle = isEmbed ? {
    background: theme.card,
    border: `1px solid ${theme.cardBorder}`,
    borderRadius: 12,
    boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
    padding: 24,
  } : null;

  // Lang switcher (vetem ne embed mode)
  const LangSwitch = !isEmbed ? null : (
    <div className="flex items-center gap-1 text-xs" data-testid="lang-switch">
      {["sq", "en", "it"].map((code) => (
        <button
          key={code}
          type="button"
          onClick={() => setLang(code)}
          data-testid={`lang-${code}`}
          className="px-2 py-1 rounded-full transition-colors"
          style={{
            background: lang === code ? theme.primary : "transparent",
            color: lang === code ? theme.textOnPrimary : theme.textMuted,
            border: `1px solid ${lang === code ? theme.primary : theme.cardBorder}`,
            fontFamily: theme.fontStack,
            fontWeight: 500,
          }}
        >
          {code.toUpperCase()}
        </button>
      ))}
    </div>
  );

  // ─── Render: Confirmation ────────────────────────────────────────────────
  if (confirmation) {
    return (
      <div
        className={`${isEmbed ? "" : "min-h-screen bg-[#FDFBF7]"} flex items-center justify-center p-6`}
        style={embedStyles}
        data-testid="booking-confirmation"
      >
        <div
          className={isEmbed ? "" : "surface-card p-10 max-w-lg w-full text-center"}
          style={isEmbed ? { ...embedCardStyle, maxWidth: 520, width: "100%", textAlign: "center", padding: 40 } : null}
        >
          <div
            className="mx-auto flex h-16 w-16 items-center justify-center rounded-full mb-6"
            style={isEmbed
              ? { background: `${theme.primary}1a`, color: theme.primary }
              : { background: "#4A8B7126", color: "#2C4C3B" }}
          >
            <CheckCircle2 size={32} />
          </div>
          <div className="label-eyebrow" style={isEmbed ? { color: theme.textMuted, letterSpacing: "0.2em", fontSize: 11, textTransform: "uppercase" } : null}>
            {t.confirmedEyebrow}
          </div>
          <h1
            className="mt-2 text-3xl font-semibold tracking-tight"
            style={isEmbed ? { color: theme.text, fontFamily: theme.fontStack } : null}
          >
            {t.thanks}
          </h1>
          <p className="mt-3" style={isEmbed ? { color: theme.textMuted } : { color: "#5C5C5C" }}>
            {t.confirmedMsgPre}<br/>
            <span className="font-semibold" style={isEmbed ? { color: theme.text } : { color: "#1A1A1A" }}>
              {new Date(confirmation.start_time).toLocaleString(t.locale, { dateStyle: "full", timeStyle: "short", timeZone: CLINIC_TZ })}
            </span>
          </p>
          <p className="mt-4 text-sm" style={isEmbed ? { color: theme.textMuted } : { color: "#5C5C5C" }}>
            {t.contactNote(form.phone)}
          </p>
          {isEmbed ? (
            <button
              type="button"
              onClick={() => { setConfirmation(null); setSelectedSlot(null); setForm({ first_name: "", last_name: "", phone: "", email: "", reason: "" }); }}
              className="mt-8 inline-flex items-center px-5 py-2 rounded-full transition-colors"
              style={{
                background: "transparent",
                color: theme.primary,
                border: `1px solid ${theme.primary}`,
                fontFamily: theme.fontStack,
                fontWeight: 500,
              }}
              data-testid="book-another-btn"
            >
              {t.bookAnother}
            </button>
          ) : (
            <Link to="/portal" onClick={() => setConfirmation(null)} className="btn-ghost mt-8 inline-flex">
              {t.bookAnother}
            </Link>
          )}
        </div>
      </div>
    );
  }

  // ─── Helpers për inline-style buttons (embed) ────────────────────────────
  const dateBtnStyle = (isSel) => isEmbed ? {
    display: "flex", flexDirection: "column", alignItems: "center",
    border: `1px solid ${isSel ? theme.primary : theme.cardBorder}`,
    borderRadius: 12, padding: 10,
    background: isSel ? theme.primary : "transparent",
    color: isSel ? theme.textOnPrimary : theme.text,
    fontFamily: theme.fontStack,
    cursor: "pointer",
    transition: "all 0.15s ease",
  } : undefined;

  const slotBtnStyle = (isSel, disabled, status) => {
    if (!isEmbed) return undefined;
    if (disabled) {
      return {
        border: `1px solid ${theme.cardBorder}`,
        background: theme.surfaceWarm,
        color: "#A39E92",
        borderRadius: 8, padding: "8px 12px", fontSize: 14, fontWeight: 500,
        textDecoration: status === "booked" ? "line-through" : "none",
        cursor: "not-allowed",
        fontFamily: theme.fontStack,
      };
    }
    return {
      border: `1px solid ${isSel ? theme.primary : theme.cardBorder}`,
      background: isSel ? theme.primary : "transparent",
      color: isSel ? theme.textOnPrimary : theme.text,
      borderRadius: 8, padding: "8px 12px", fontSize: 14, fontWeight: 500,
      cursor: "pointer", transition: "all 0.15s ease",
      fontFamily: theme.fontStack,
    };
  };

  const primaryBtnStyle = isEmbed ? {
    width: "100%", height: 48,
    background: theme.primary, color: theme.textOnPrimary,
    border: "none", borderRadius: 999,
    fontFamily: theme.fontStack, fontWeight: 600, fontSize: 15,
    cursor: submitting || !selectedSlot ? "not-allowed" : "pointer",
    opacity: submitting || !selectedSlot ? 0.6 : 1,
    transition: "background 0.15s ease",
  } : null;

  const inputStyle = isEmbed ? {
    border: `1px solid ${theme.cardBorder}`,
    borderRadius: 8,
    fontFamily: theme.fontStack,
    color: theme.text,
    background: "#FFFFFF",
  } : undefined;

  const labelStyle = isEmbed ? {
    color: theme.textMuted,
    letterSpacing: "0.15em",
    fontSize: 10,
    textTransform: "uppercase",
    fontFamily: theme.fontStack,
    fontWeight: 600,
  } : undefined;

  // ─── Render: Main Booking Form ───────────────────────────────────────────
  return (
    <div
      className={isEmbed ? "" : "min-h-screen bg-[#FDFBF7]"}
      style={embedStyles}
      data-testid="public-booking-page"
      data-embed={isEmbed ? "1" : "0"}
      data-lang={lang}
    >
      {/* Header — fshihet ne embed mode (klienti ka header-in e tij) */}
      {!isEmbed && (
        <header className="border-b border-[#E2DFD8] bg-white">
          <div className="mx-auto max-w-5xl flex items-center justify-between px-6 py-5">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#2C4C3B] text-[#FDFBF7]">
                <Sparkles size={18} />
              </div>
              <div>
                <div className="font-semibold tracking-tight" data-testid="branding-clinic-name">{branding.clinic_name}</div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-[#5C5C5C]">{t.onlineBooking}</div>
              </div>
            </div>
            <Link to="/login" className="text-sm text-[#5C5C5C] hover:text-[#2C4C3B]" data-testid="staff-login-link">
              {t.staffLogin}
            </Link>
          </div>
        </header>
      )}

      {/* Embed-mode top bar: lang switcher only */}
      {isEmbed && (
        <div className="flex justify-end mb-3" style={{ padding: "8px 16px 0" }}>
          {LangSwitch}
        </div>
      )}

      <main className={isEmbed ? "px-4 pb-6 pt-2" : "mx-auto max-w-5xl px-6 py-12"}>
        {!isEmbed && (
          <>
            <div className="label-eyebrow">{t.clinicLabel}</div>
            <h1 className="mt-2 text-4xl font-semibold tracking-tight sm:text-5xl">{t.title}</h1>
            <p className="mt-3 text-[#5C5C5C] max-w-2xl">{t.subtitle}</p>
          </>
        )}

        <div className={`${isEmbed ? "" : "mt-10"} grid grid-cols-1 lg:grid-cols-2 gap-6`}>
          {/* Date + slot picker */}
          <div className={isEmbed ? "" : "surface-card p-6"} style={embedCardStyle || undefined}>
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="label-eyebrow" style={labelStyle}>{t.step1}</div>
                <h2
                  className="mt-1 text-xl font-semibold tracking-tight"
                  style={isEmbed ? { color: theme.text, fontFamily: theme.fontStack } : null}
                >
                  {t.pickDate}
                </h2>
              </div>
              <div className="flex gap-1">
                <button
                  onClick={() => moveWeek(-1)}
                  className={isEmbed ? "" : "rounded-full p-2 hover:bg-[#EAE4D9]"}
                  style={isEmbed ? { padding: 8, borderRadius: 999, border: "none", background: "transparent", cursor: "pointer", color: theme.textMuted } : null}
                  data-testid="week-prev"
                  aria-label="prev"
                >
                  <ChevronLeft size={16} />
                </button>
                <button
                  onClick={() => moveWeek(1)}
                  className={isEmbed ? "" : "rounded-full p-2 hover:bg-[#EAE4D9]"}
                  style={isEmbed ? { padding: 8, borderRadius: 999, border: "none", background: "transparent", cursor: "pointer", color: theme.textMuted } : null}
                  data-testid="week-next"
                  aria-label="next"
                >
                  <ChevronRight size={16} />
                </button>
              </div>
            </div>

            <div className="grid grid-cols-7 gap-2">
              {nextDays.map((d) => {
                const isSel = d.toDateString() === selectedDate.toDateString();
                const cls = isEmbed
                  ? ""
                  : `flex flex-col items-center rounded-xl border p-2 transition-colors ${
                      isSel ? "border-[#2C4C3B] bg-[#2C4C3B] text-[#FDFBF7]" : "border-[#E2DFD8] hover:border-[#2C4C3B] hover:text-[#2C4C3B]"
                    }`;
                return (
                  <button
                    key={d.toISOString()}
                    onClick={() => setSelectedDate(d)}
                    data-testid={`date-${d.getDate()}`}
                    className={cls}
                    style={dateBtnStyle(isSel)}
                  >
                    <span className="text-[10px] uppercase tracking-wider opacity-70">{t.daysShort[(d.getDay() + 6) % 7]}</span>
                    <span className="mt-1 text-xl font-semibold">{d.getDate()}</span>
                    <span className="text-[10px] uppercase opacity-70">{t.monthsShort[d.getMonth()]}</span>
                  </button>
                );
              })}
            </div>

            <div
              className="mt-6 mb-2 label-eyebrow flex items-center gap-2"
              style={labelStyle}
            >
              <Calendar size={14} /> {fmtDateLong(selectedDate)}
            </div>

            {loadingSlots ? (
              <div className="text-center text-sm py-8" style={isEmbed ? { color: theme.textMuted } : { color: "#5C5C5C" }}>{t.searchingSlots}</div>
            ) : allSlots.length === 0 ? (
              <div className="text-center text-sm py-8" style={isEmbed ? { color: theme.textMuted } : { color: "#5C5C5C" }}>{t.clinicClosed}</div>
            ) : (
              <>
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                  {allSlots.map(({ start_time: s, status }) => {
                    const isSel = s === selectedSlot;
                    const disabled = status !== "available";
                    const label = fmtTime(s);
                    let title = "";
                    let classes = "";
                    if (disabled) {
                      if (status === "booked") {
                        title = t.busy;
                        classes = "border-[#E2DFD8] bg-[#F4EFE7] text-[#A39E92] line-through cursor-not-allowed";
                      } else if (status === "break") {
                        title = t.breakLunch;
                        classes = "border-[#E2DFD8] bg-[#F4EFE7] text-[#A39E92] cursor-not-allowed";
                      } else {
                        title = t.outsideHours;
                        classes = "border-[#E2DFD8] bg-[#F4EFE7] text-[#A39E92] cursor-not-allowed";
                      }
                    } else if (isSel) {
                      classes = "border-[#2C4C3B] bg-[#2C4C3B] text-[#FDFBF7]";
                    } else {
                      classes = "border-[#E2DFD8] hover:border-[#2C4C3B] hover:text-[#2C4C3B]";
                    }
                    return (
                      <button
                        key={s}
                        type="button"
                        onClick={() => !disabled && setSelectedSlot(s)}
                        disabled={disabled}
                        title={title}
                        data-testid={`slot-${s}`}
                        data-status={status}
                        aria-disabled={disabled}
                        className={isEmbed ? "" : `rounded-lg border px-3 py-2 text-sm font-medium transition-colors ${classes}`}
                        style={slotBtnStyle(isSel, disabled, status)}
                      >
                        {label}
                        {status === "break" && (
                          <span className="block text-[10px] mt-0.5 uppercase tracking-wider opacity-80">{t.lunch}</span>
                        )}
                        {status === "booked" && (
                          <span className="block text-[10px] mt-0.5 uppercase tracking-wider opacity-80">{t.busy}</span>
                        )}
                      </button>
                    );
                  })}
                </div>
                {slots.length === 0 && (
                  <div className="mt-3 text-center text-xs" style={isEmbed ? { color: theme.error } : { color: "#9E3C3C" }}>
                    {t.noSlots}
                  </div>
                )}
                <div className="mt-4 flex flex-wrap gap-3 text-[11px]" style={isEmbed ? { color: theme.textMuted } : { color: "#5C5C5C" }} data-testid="slot-legend">
                  <span className="flex items-center gap-1.5">
                    <span className="inline-block w-3 h-3 rounded border" style={isEmbed ? { borderColor: theme.cardBorder } : { borderColor: "#E2DFD8" }} /> {t.legendFree}
                  </span>
                  <span className="flex items-center gap-1.5">
                    <span className="inline-block w-3 h-3 rounded" style={isEmbed ? { background: theme.primary } : { background: "#2C4C3B" }} /> {t.legendSelected}
                  </span>
                  <span className="flex items-center gap-1.5">
                    <span className="inline-block w-3 h-3 rounded border" style={isEmbed ? { background: theme.surfaceWarm, borderColor: theme.cardBorder } : { background: "#F4EFE7", borderColor: "#E2DFD8" }} /> {t.legendBusy}
                  </span>
                </div>
              </>
            )}
          </div>

          {/* Form */}
          <div className={isEmbed ? "" : "surface-card p-6"} style={embedCardStyle || undefined}>
            <div className="label-eyebrow" style={labelStyle}>{t.step2}</div>
            <h2
              className="mt-1 text-xl font-semibold tracking-tight mb-4"
              style={isEmbed ? { color: theme.text, fontFamily: theme.fontStack } : null}
            >
              {t.yourDetails}
            </h2>

            <form onSubmit={submit} className="space-y-3" data-testid="booking-form">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="label-eyebrow" style={labelStyle}>{t.firstName}</Label>
                  <Input required value={form.first_name} onChange={(e) => setForm({...form, first_name: e.target.value})}
                         className="mt-2" style={inputStyle} data-testid="public-first-name" />
                </div>
                <div>
                  <Label className="label-eyebrow" style={labelStyle}>{t.lastName}</Label>
                  <Input required value={form.last_name} onChange={(e) => setForm({...form, last_name: e.target.value})}
                         className="mt-2" style={inputStyle} data-testid="public-last-name" />
                </div>
              </div>
              <div>
                <Label className="label-eyebrow flex items-center gap-1" style={labelStyle}><Phone size={12} /> {t.phone}</Label>
                <Input required type="tel" value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})}
                       placeholder={t.phonePh} className="mt-2" style={inputStyle} data-testid="public-phone" />
              </div>
              <div>
                <Label className="label-eyebrow flex items-center gap-1" style={labelStyle}><Mail size={12} /> {t.email}</Label>
                <Input type="email" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})}
                       className="mt-2" style={inputStyle} data-testid="public-email" />
              </div>
              <div>
                <Label className="label-eyebrow" style={labelStyle}>{t.reason}</Label>
                <Input value={form.reason} onChange={(e) => setForm({...form, reason: e.target.value})}
                       placeholder={t.reasonPh} className="mt-2" style={inputStyle} data-testid="public-reason" />
              </div>

              <div
                className={isEmbed ? "" : "rounded-lg bg-[#EAE4D9]/50 p-4"}
                style={isEmbed ? { background: theme.surface, padding: 16, borderRadius: 10 } : null}
              >
                <div className="label-eyebrow flex items-center gap-1" style={labelStyle}><User size={12} /> {t.summary}</div>
                <div className="mt-1 text-sm" style={isEmbed ? { color: theme.text } : null}>
                  {selectedSlot
                    ? t.apptOn(fmtDateLong(selectedDate), fmtTime(selectedSlot))
                    : <span style={isEmbed ? { color: theme.textMuted } : { color: "#5C5C5C" }}>{t.pickSlotFirst}</span>}
                </div>
              </div>

              {error && (
                <div
                  className={isEmbed ? "" : "rounded-lg border border-[#9E3C3C]/30 bg-[#9E3C3C]/10 px-4 py-2 text-sm text-[#9E3C3C]"}
                  style={isEmbed ? { borderRadius: 8, padding: "8px 14px", background: `${theme.error}1a`, color: theme.error, border: `1px solid ${theme.error}50`, fontSize: 14 } : null}
                  data-testid="booking-error"
                >
                  {error}
                </div>
              )}

              <button
                type="submit"
                className={isEmbed ? "" : "btn-primary w-full h-12"}
                style={primaryBtnStyle || undefined}
                disabled={submitting || !selectedSlot}
                data-testid="booking-submit"
              >
                {submitting ? t.booking : t.confirm}
              </button>
              <p className="text-[10px] text-center" style={isEmbed ? { color: theme.textMuted } : { color: "#5C5C5C" }}>
                {t.consent}
              </p>
            </form>
          </div>
        </div>
      </main>

      {!isEmbed && (
        <footer className="border-t border-[#E2DFD8] mt-12 py-6 text-center text-xs text-[#5C5C5C]">
          {t.footer(branding.clinic_name, new Date().getFullYear())}
        </footer>
      )}

      {!isEmbed && <AIChatWidget source="portal" />}
    </div>
  );
}
